import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FileInputComponent } from '@catch/file-input';
import {
  ButtonComponent,
  InlineInfoComponent,
  ModalComponent,
  SalaryInputComponent,
  SkillLevelTagComponent,
  SkillLevelTagOption,
} from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { DateInputComponent } from '../../../components/snippets/date-input/date-input.component';
import { fileTypes } from '../../../constants/filetypes';
import { ApplicantSkillLevel } from '../../../enums/applicant-skill-level.enum';
import {
  PLATFORM_PROFILE_SKILL_LEVELS,
  PlatformProfileSkill,
} from '../../models/platform.models';
import { PlatformDocumentKind } from '../../enums/platform-document-kind.enum';
import { PlatformApplyWithProfileMode } from '../../enums/platform-apply-with-profile-mode.enum';
import { PlatformApplyWithProfileService } from '../../services/platform-apply-with-profile.service';
import {
  PlatformApplyWithProfileContext,
  PlatformJobsService,
} from '../../services/platform-jobs.service';
import { PlatformProfileService } from '../../services/platform-profile.service';

@Component({
  selector: 'app-platform-apply-with-profile-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    ButtonComponent,
    FileInputComponent,
    InlineInfoComponent,
    ModalComponent,
    SalaryInputComponent,
    SkillLevelTagComponent,
    TranslocoPipe,
    DateInputComponent,
  ],
  templateUrl: './platform-apply-with-profile-modal.component.html',
  styleUrl: './platform-apply-with-profile-modal.component.scss',
})
export class PlatformApplyWithProfileModalComponent {
  private readonly fb = inject(FormBuilder);
  private readonly toastsService = inject(ToastsService);
  readonly applyService = inject(PlatformApplyWithProfileService);
  readonly jobs = inject(PlatformJobsService);
  readonly profile = inject(PlatformProfileService);

  readonly PlatformApplyWithProfileMode = PlatformApplyWithProfileMode;
  readonly fileTypes = fileTypes;
  readonly submitAttempt = signal(false);
  readonly formNonce = signal(0);
  readonly privacyExpanded = signal(false);
  readonly skills = signal<PlatformProfileSkill[]>([]);
  readonly missingSkillNames = signal<string[]>([]);

  readonly skillLevelOptions: SkillLevelTagOption[] = PLATFORM_PROFILE_SKILL_LEVELS.map(
    (level) => ({
      value: level,
      label: `ct-library.constants.skill-level.${level}`,
    })
  );

  readonly form: FormGroup = this.fb.group({
    firstName: this.fb.control(''),
    lastName: this.fb.control(''),
    phone: this.fb.control(''),
    city: this.fb.control(''),
    salary: this.fb.control<number | null>(null),
    startDate: this.fb.control<number | null>(null),
    cv: this.fb.control<File | null>(null),
    coverLetter: this.fb.control<File | File[] | null>(null),
    certificates: this.fb.control<File[] | File | null>(null),
    privacyAccepted: this.fb.control(false, Validators.requiredTrue),
  });

  private readonly modal = viewChild.required<ModalComponent>('modal');

  readonly context = this.applyService.context;

  readonly mainCvName = computed(() => {
    this.profile.profile();
    return (
      this.profile
        .profile()
        .documents.find((document) => document.kind === PlatformDocumentKind.MAIN_CV)
        ?.name ?? null
    );
  });

  readonly certificateDocuments = computed(() =>
    this.profile
      .profile()
      .documents.filter(
        (document) =>
          document.kind === PlatformDocumentKind.CERTIFICATE ||
          document.kind === PlatformDocumentKind.OTHER
      )
  );

  readonly coverLetterDocuments = computed(() =>
    this.profile
      .profile()
      .documents.filter(
        (document) => document.kind === PlatformDocumentKind.COVER_LETTER
      )
  );

  readonly showPhoneInput = computed(() => {
    const context = this.context();
    const profile = this.profile.profile();
    return Boolean(context && !profile.phone);
  });

  readonly showNameInputs = computed(() => {
    const context = this.context();
    const profile = this.profile.profile();
    return Boolean(context && (!profile.firstName || !profile.lastName));
  });

  readonly showCityInput = computed(() => {
    const context = this.context();
    const profile = this.profile.profile();
    return Boolean(context && !profile.city);
  });

  readonly showStartDate = computed(() => {
    const context = this.context();
    const profile = this.profile.profile();
    return Boolean(
      context?.requirements.startDate && profile.startDate == null
    );
  });

  readonly showSalary = computed(() => {
    const context = this.context();
    const profile = this.profile.profile();
    return Boolean(
      context?.requirements.salary &&
        profile.salaryMin == null &&
        profile.salaryMax == null
    );
  });

  readonly showCv = computed(() => Boolean(this.context()?.requirements.cv));

  readonly showCoverLetter = computed(() => {
    const context = this.context();
    if (!context?.requirements.coverLetter) {
      return false;
    }
    return this.coverLetterDocuments().length === 0;
  });

  readonly showCertificates = computed(() => {
    const context = this.context();
    if (!context?.requirements.certificates) {
      return false;
    }
    return this.certificateDocuments().length === 0;
  });

  readonly displayName = computed(() => {
    const profile = this.profile.profile();
    return [profile.firstName, profile.lastName].filter(Boolean).join(' ');
  });

  constructor() {
    effect(() => {
      const context = this.applyService.context();
      const modal = this.modal();
      untracked(() => {
        if (context && modal) {
          this.openFor(context);
        }
      });
    });
  }

  /**
   * Returns the translation key for a skill level.
   * @param level - Skill level value
   * @returns Translation key
   */
  skillLevelLabel(level: string): string {
    return `ct-library.constants.skill-level.${level}`;
  }

  /**
   * Updates the level of a skill listed in the modal.
   * @param name - Skill name
   * @param level - Selected level value
   */
  updateSkillLevel(name: string, level: string): void {
    this.skills.update((skills) =>
      skills.map((skill) =>
        skill.name === name ? { ...skill, level: level as ApplicantSkillLevel } : skill
      )
    );
  }

  /**
   * Salary control for the shared salary input.
   * @returns Salary form control
   */
  salaryControl(): UntypedFormControl {
    return this.form.controls['salary'] as UntypedFormControl;
  }

  /**
   * Start-date control for the shared date input.
   * @returns Start-date form control
   */
  startDateControl(): FormControl<number> {
    return this.form.controls['startDate'] as FormControl<number>;
  }

  /**
   * CV file control for the shared file input.
   * @returns CV form control
   */
  cvControl(): UntypedFormControl {
    return this.form.controls['cv'] as UntypedFormControl;
  }

  /**
   * Cover-letter file control for the shared file input.
   * @returns Cover-letter form control
   */
  coverLetterControl(): UntypedFormControl {
    return this.form.controls['coverLetter'] as UntypedFormControl;
  }

  /**
   * Certificates file control for the shared file input.
   * @returns Certificates form control
   */
  certificatesControl(): UntypedFormControl {
    return this.form.controls['certificates'] as UntypedFormControl;
  }

  /**
   * Closes the modal without submitting.
   */
  close(): void {
    this.modal().close();
    this.applyService.close();
  }

  /**
   * Toggles the long privacy copy.
   */
  togglePrivacy(): void {
    this.privacyExpanded.update((expanded) => !expanded);
  }

  /**
   * Validates and submits apply-with-profile data.
   */
  submit(): void {
    this.submitAttempt.set(true);
    this.applyCvValidator();
    if (this.form.invalid) {
      return;
    }
    const context = this.context();
    if (!context) {
      return;
    }
    this.jobs.submitApplyWithProfile(context, {
      firstName: this.form.controls['firstName'].value || undefined,
      lastName: this.form.controls['lastName'].value || undefined,
      phone: this.form.controls['phone'].value || undefined,
      city: this.form.controls['city'].value || undefined,
      salary: this.showSalary() ? this.form.controls['salary'].value : undefined,
      startDate: this.showStartDate()
        ? this.form.controls['startDate'].value
        : undefined,
      skills: this.skills(),
      certificateNames: this.fileNames(this.form.controls['certificates'].value),
      cvFile: this.asFile(this.form.controls['cv'].value),
      coverLetterFile: this.asFile(this.form.controls['coverLetter'].value),
      certificateFiles: this.asFiles(this.form.controls['certificates'].value),
    });
    this.toastsService.success(
      context.mode === PlatformApplyWithProfileMode.FOLLOW_UP
        ? 'jobboard.platform.apply-with-profile.follow-up-toast'
        : 'jobboard.platform.apply-with-profile.submit-toast'
    );
    this.close();
  }

  /**
   * Opens and hydrates the modal for the given context.
   * @param context - Job context
   */
  private openFor(context: PlatformApplyWithProfileContext): void {
    const profile = this.profile.profile();
    this.submitAttempt.set(false);
    this.privacyExpanded.set(false);
    const missing = context.requiredSkills.filter(
      (name) =>
        !profile.skills.some(
          (skill) => skill.name.toLowerCase() === name.toLowerCase()
        )
    );
    this.missingSkillNames.set(missing);
    this.skills.set([
      ...profile.skills,
      ...missing.map((name) => ({
        name,
        level: ApplicantSkillLevel.GOOD,
      })),
    ]);
    this.form.reset({
      firstName: '',
      lastName: '',
      phone: '',
      city: '',
      salary: null,
      startDate: null,
      cv: null,
      coverLetter: null,
      certificates: null,
      privacyAccepted: false,
    });
    this.applyValidators(context);
    this.formNonce.update((nonce) => nonce + 1);
    this.modal().open();
  }

  /**
   * Applies required validators for missing profile and job fields.
   * @param context - Job context
   */
  private applyValidators(context: PlatformApplyWithProfileContext): void {
    const profile = this.profile.profile();
    this.toggleRequired('firstName', !profile.firstName);
    this.toggleRequired('lastName', !profile.lastName);
    this.toggleRequired('phone', !profile.phone);
    this.toggleRequired('city', false);
    this.toggleRequired(
      'startDate',
      context.requirements.startDate && profile.startDate == null
    );
    this.toggleRequired(
      'salary',
      context.requirements.salary &&
        profile.salaryMin == null &&
        profile.salaryMax == null
    );
    const coverLetterControl = this.form.controls['coverLetter'];
    coverLetterControl.clearValidators();
    if (context.requirements.coverLetter && this.coverLetterDocuments().length === 0) {
      coverLetterControl.addValidators(this.fileRequiredValidator());
    }
    coverLetterControl.updateValueAndValidity();
    this.form.controls['privacyAccepted'].setValidators(Validators.requiredTrue);
    this.form.controls['privacyAccepted'].updateValueAndValidity();
    this.applyCvValidator();
    const certificatesControl = this.form.controls['certificates'];
    certificatesControl.clearValidators();
    if (context.requirements.certificates && this.certificateDocuments().length === 0) {
      certificatesControl.addValidators(this.fileRequiredValidator());
    }
    certificatesControl.updateValueAndValidity();
  }

  /**
   * Requires a CV when the job asks for one and none exists on the profile.
   */
  private applyCvValidator(): void {
    const context = this.context();
    const cvControl = this.form.controls['cv'];
    cvControl.clearValidators();
    if (context?.requirements.cv && !this.mainCvName()) {
      cvControl.addValidators(this.fileRequiredValidator());
    }
    cvControl.updateValueAndValidity();
  }

  /**
   * Adds or removes the required validator on a named control.
   * @param name - Control name
   * @param required - Whether the field is required
   */
  private toggleRequired(name: string, required: boolean): void {
    const control = this.form.controls[name];
    control.clearValidators();
    if (required) {
      control.addValidators(Validators.required);
    }
    control.updateValueAndValidity();
  }

  /**
   * Requires at least one uploaded file.
   * @returns Validator
   */
  private fileRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (Array.isArray(value)) {
        return value.length > 0 ? null : { required: true };
      }
      return value ? null : { required: true };
    };
  }

  /**
   * Collects file names from a file-input control value.
   * @param value - Control value
   * @returns File names
   */
  private fileNames(value: File[] | File | null): string[] {
    if (Array.isArray(value)) {
      return value.filter((file) => file instanceof File).map((file) => file.name);
    }
    return value instanceof File ? [value.name] : [];
  }

  /**
   * Returns a single file from a file-input value.
   * @param value - Control value
   * @returns File or null
   */
  private asFile(value: unknown): File | null {
    if (value instanceof File) {
      return value;
    }
    if (Array.isArray(value) && value[0] instanceof File) {
      return value[0];
    }
    return null;
  }

  /**
   * Returns files from a file-input value.
   * @param value - Control value
   * @returns Files
   */
  private asFiles(value: unknown): File[] {
    if (Array.isArray(value)) {
      return value.filter((file): file is File => file instanceof File);
    }
    return value instanceof File ? [value] : [];
  }
}
